/**
 * Ted #30 (#1109 AC4, lane LR) — the series afterwards: its rule, every
 * occurrence with its status, and the vaccine problems on the dates still
 * to come. Opened from a Bookings row's "Part of a recurring series" line.
 * Five states: loading (skeleton) · error (retry) · empty (no live dates)
 * · default · (held / detached / cancelled rows say so, never a bare count).
 */
import { useCallback, useEffect, useState } from 'react';
import { seriesApi, type SeriesViewResponse } from '../../lib/api.js';
import { formatReservationDate } from './confirm-pending.js';
import { issueLine } from './RecurringBookingDialog.js';
import './Recurring.css';

export interface RecurringSeriesPanelProps {
  apiUrl: string;
  seriesId: string;
  onClose: () => void;
}

function statusLabel(o: SeriesViewResponse['occurrences'][number]): { text: string; mod: string } {
  if (o.status === 'cancelled') return { text: 'Cancelled', mod: 'cancelled' };
  if (o.review_held) return { text: 'Needs staff review', mod: 'held' };
  if (o.status === 'checked_out') return { text: 'Checked out', mod: 'done' };
  if (o.status === 'checked_in') return { text: 'Checked in', mod: 'done' };
  if (o.status === 'confirmed') return { text: 'Confirmed', mod: 'ok' };
  if (o.status === 'no_show') return { text: 'No-show', mod: 'cancelled' };
  return { text: 'Pending', mod: 'pending' };
}

export function notificationSentence(rows: SeriesViewResponse['notifications']): string | null {
  const by = new Map(rows.map((r) => [r.type, r.count]));
  const parts: string[] = [];
  if (by.get('series_confirmation')) parts.push('one confirmation');
  if (by.get('series_waitlist')) parts.push('one note about dates that were not available');
  if (by.get('series_vaccine_issue')) parts.push('one vaccination notice');
  if (parts.length === 0) return null;
  return `The client was sent ${parts.join(', ')} — for the whole series.`;
}

export function RecurringSeriesPanel({ apiUrl, seriesId, onClose }: RecurringSeriesPanelProps): JSX.Element {
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready'; view: SeriesViewResponse }>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const res = await seriesApi.get(apiUrl, seriesId);
    if (res.data) setState({ kind: 'ready', view: res.data });
    else setState({ kind: 'error', message: res.error ?? 'We could not load this series.' });
  }, [apiUrl, seriesId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="series-panel-title" className="ferro-blocking-dialog__overlay" data-testid="series-panel">
      <div className="ferro-blocking-dialog__panel ferro-series">
        <h2 id="series-panel-title">
          {state.kind === 'ready'
            ? `${state.view.dogs.map((d) => d.name).join(', ') || 'Series'} · ${state.view.service_type?.display_name ?? ''} ${state.view.rule_text}`
            : 'Recurring series'}
        </h2>

        {state.kind === 'loading' ? (
          <ul className="ferro-recurring__plan" aria-busy="true" data-testid="series-panel-skeleton">
            <li className="ferro-recurring__skeleton" />
            <li className="ferro-recurring__skeleton" />
            <li className="ferro-recurring__skeleton" />
          </ul>
        ) : null}

        {state.kind === 'error' ? (
          <div className="ferro-banner ferro-banner--warning" role="alert" data-testid="series-panel-error">
            <p>{state.message}</p>
            <button type="button" className="ferro-btn ferro-btn--ghost" onClick={() => void load()} data-testid="series-panel-retry">
              Try again
            </button>
          </div>
        ) : null}

        {state.kind === 'ready' ? (
          <>
            <p className="ferro-series__meta" data-testid="series-panel-meta">
              First date {formatReservationDate(state.view.series.rule.start_date)}
              {state.view.series.rule.until_date ? ` · until ${formatReservationDate(state.view.series.rule.until_date)}` : ''}
              {state.view.series.rule.occurrence_count ? ` · ${state.view.series.rule.occurrence_count} dates` : ''}
              {state.view.series.drop_off_time ? ` · drop-off ${state.view.series.drop_off_time}` : ''}
              {state.view.series.pick_up_time ? ` · pick-up ${state.view.series.pick_up_time}` : ''}
            </p>
            {(() => {
              const sent = notificationSentence(state.view.notifications);
              return sent ? (
                <p className="ferro-series__meta" data-testid="series-panel-notifications">
                  {sent}
                </p>
              ) : null;
            })()}
            {state.view.occurrences.length === 0 ? (
              <p className="ferro-series__meta" data-testid="series-panel-empty">
                No reservations are attached to this series.
              </p>
            ) : (
              <ul className="ferro-recurring__plan" aria-label="Series dates" data-testid="series-panel-list">
                {state.view.occurrences.map((o) => {
                  const issue = o.status === 'cancelled' ? null : issueLine(o.vaccine_issues);
                  const label = statusLabel(o);
                  const mod = o.status === 'cancelled' ? 'refused' : o.review_held ? 'held' : issue ? 'issue' : 'created';
                  return (
                    <li key={o.booking_id} className={`ferro-recurring__plan-row ferro-recurring__plan-row--${mod}`} data-testid={`series-panel-${o.date}`}>
                      <span className="ferro-recurring__plan-date">{formatReservationDate(o.date)}</span>
                      <span className={`ferro-series__pill${label.mod === 'held' ? ' ferro-series__pill--held' : ''}${label.mod === 'cancelled' ? ' ferro-series__pill--cancelled' : ''}`}>
                        {label.text}
                      </span>
                      {o.detached_at ? <span className="ferro-series__pill ferro-series__pill--detached">Changed on its own</span> : null}
                      {o.review_held && o.review_hold_reason ? <span className="ferro-recurring__plan-issue">{o.review_hold_reason}</span> : null}
                      {issue ? <span className="ferro-recurring__plan-issue">Vaccination: {issue}</span> : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        ) : null}

        <div className="ferro-blocking-dialog__actions">
          <button type="button" className="ferro-btn ferro-btn--primary" onClick={onClose} data-testid="series-panel-close">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
